import { useState, useEffect, useCallback } from 'react';
import { fetchScopeSummary, ScopeSummaryResponse } from '../services/cmdb/ScopeService';

export type ScopeSummaryState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'success'; data: ScopeSummaryResponse }
  | { phase: 'error'; message: string };

/**
 * Fetches and caches the scope coverage summary for the given support group.
 * Refreshes when `supportGroup` changes or `refresh()` is called.
 */
export function useScopeSummary(supportGroup: string | null) {
  const [state, setState] = useState<ScopeSummaryState>({ phase: 'idle' });

  const load = useCallback(async (group: string) => {
    setState({ phase: 'loading' });
    try {
      const data = await fetchScopeSummary(group);
      setState({ phase: 'success', data });
    } catch (err: any) {
      setState({ phase: 'error', message: err?.message ?? 'Failed to load scope summary' });
    }
  }, []);

  useEffect(() => {
    if (!supportGroup) {
      setState({ phase: 'idle' });
      return;
    }
    load(supportGroup);
  }, [supportGroup, load]);

  const refresh = useCallback(() => {
    if (supportGroup) load(supportGroup);
  }, [supportGroup, load]);

  return { state, refresh };
}
